import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigableProps } from '../../utils/navigation';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  useTheme,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd';
import { useNotification } from '../../hooks/NotificationContext';
import { usePermissions } from '../../hooks/usePermissions';
import * as P from '../../constants/permissions';
import { projekteService } from '../../services/projekteService';
import angeboteService from '../../services/angeboteService';
import { statusService } from '../../services/lookupService';
import { AngebotList, ProjektList, Status } from '../../types';
import {
  ANGEBOT_BOARD_COLUMN_ID,
  angebotOhneProjektSichtbar,
  angebotPipelineLabel,
  boardRowKey,
  isFestpreisProjektTyp,
  mapAngebotToProjektRow,
  projektTypLabel,
} from '../../utils/projektTyp';

interface KanbanColumn {
  id: string;
  title: string;
  statusId: number | null;
  items: ProjektList[];
}

const NO_STATUS_KEY = 'no-status';

const typChipSx = (typ: string | undefined, isDark: boolean) => {
  const label = projektTypLabel(typ);
  if (label === 'Kontingent') {
    return {
      label,
      icon: <AccountBalanceWalletIcon sx={{ fontSize: '14px !important' }} />,
      bgcolor: isDark ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.1)',
      color: isDark ? '#ffb74d' : '#e65100',
      border: isDark ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.3)',
    };
  }
  if (label === 'Festpreis') {
    return {
      label,
      icon: <RequestQuoteIcon sx={{ fontSize: '14px !important' }} />,
      bgcolor: isDark ? 'rgba(0, 137, 123, 0.18)' : 'rgba(0, 137, 123, 0.1)',
      color: isDark ? '#4db6ac' : '#00695c',
      border: isDark ? 'rgba(0, 137, 123, 0.35)' : 'rgba(0, 137, 123, 0.3)',
    };
  }
  return {
    label,
    icon: <AssignmentIcon sx={{ fontSize: '14px !important' }} />,
    bgcolor: isDark ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.1)',
    color: isDark ? '#64b5f6' : '#1565c0',
    border: isDark ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.3)',
  };
};

const ProjectKanbanView: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { showSuccess } = useNotification();
  const { hasPermission } = usePermissions();
  const canViewAngebote = hasPermission(P.ANGEBOTE_VIEW);

  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  const [loading, setLoading] = useState(true);

  // Colors
  const accent = theme.palette.primary.main;
  const bgElevated = isDark ? '#2c3358' : '#f5f7fa';
  const bgCard = isDark ? '#212744' : '#ffffff';
  const bgColumn = isDark ? '#1c203b' : '#f0f2f5';
  const borderColor = theme.palette.divider;
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [projekte, statuses, angebote] = await Promise.all([
        projekteService.list(),
        statusService.list(),
        canViewAngebote ? angeboteService.list() : Promise.resolve([] as AngebotList[]),
      ]);

      const projectRows: ProjektList[] = projekte.map((p) => ({ ...p, source: 'projekt' as const }));

      // Create columns from statuses (sorted by sort_order)
      const sortedStatuses = [...statuses].sort((a, b) => a.sort_order - b.sort_order);
      const statusColumns: KanbanColumn[] = sortedStatuses.map((s: Status) => ({
        id: String(s.id),
        title: s.name,
        statusId: s.id,
        items: projectRows.filter((p) => p.status?.id === s.id),
      }));

      // Column for projects without status
      const noStatusProjects = projectRows.filter((p) => !p.status);
      const noStatusColumn: KanbanColumn = {
        id: NO_STATUS_KEY,
        title: 'Kein Status',
        statusId: null,
        items: noStatusProjects,
      };

      const columns: KanbanColumn[] = [noStatusColumn, ...statusColumns];
      if (canViewAngebote) {
        columns.unshift({
          id: ANGEBOT_BOARD_COLUMN_ID,
          title: 'Angebot',
          statusId: null,
          items: angebote.filter((a) => angebotOhneProjektSichtbar(a, false)).map(mapAngebotToProjektRow),
        });
      }

      setColumns(columns);
    } catch {
      // Error display handled by global axios interceptor
    } finally {
      setLoading(false);
    }
  }, [canViewAngebote]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;

    // Dropped outside a droppable
    if (!destination) return;

    // Offene Angebote sind nur Anzeige, kein Projektstatus-Wechsel
    if (draggableId.startsWith('angebot-') || destination.droppableId === ANGEBOT_BOARD_COLUMN_ID) {
      return;
    }

    // Dropped in the same position
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const projektId = parseInt(draggableId.replace(/^projekt-/, ''), 10);
    const sourceColIdx = columns.findIndex((c) => c.id === source.droppableId);
    const destColIdx = columns.findIndex(
      (c) => c.id === destination.droppableId
    );

    if (sourceColIdx === -1 || destColIdx === -1) return;

    // Optimistic update
    const prevColumns = columns.map((col) => ({
      ...col,
      items: [...col.items],
    }));

    const newColumns = columns.map((col) => ({
      ...col,
      items: [...col.items],
    }));

    const [movedItem] = newColumns[sourceColIdx].items.splice(source.index, 1);
    if (!movedItem) return;

    // Update the status on the moved item
    const newStatusId = newColumns[destColIdx].statusId;
    const updatedItem: ProjektList = {
      ...movedItem,
      status: newStatusId
        ? { id: newStatusId, name: newColumns[destColIdx].title, sort_order: 0 }
        : undefined,
    };

    newColumns[destColIdx].items.splice(destination.index, 0, updatedItem);
    setColumns(newColumns);

    // Persist to backend
    try {
      await projekteService.update(projektId, {
        status_id: newStatusId ?? undefined,
      });
      showSuccess(
        `Projekt "${movedItem.name}" nach "${newColumns[destColIdx].title}" verschoben`
      );
    } catch {
      // Rollback optimistic update; error display handled by global axios interceptor
      setColumns(prevColumns);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 2,
            minHeight: 500,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: borderColor,
              borderRadius: 4,
            },
          }}
        >
          {columns.map((column) => (
            <Box
              key={column.id}
              sx={{
                minWidth: 300,
                maxWidth: 300,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Column Header */}
              <Paper
                sx={{
                  px: 2,
                  py: 1.5,
                  mb: 1,
                  borderRadius: 2,
                  bgcolor: bgElevated,
                  border: `1px solid ${borderColor}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    color: textPrimary,
                    textTransform: 'capitalize',
                  }}
                >
                  {column.title}
                </Typography>
                <Chip
                  label={column.items.length}
                  size="small"
                  sx={{
                    height: 22,
                    minWidth: 28,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    bgcolor: `${accent}20`,
                    color: accent,
                    border: `1px solid ${accent}40`,
                  }}
                />
              </Paper>

              {/* Droppable Area */}
              <Droppable droppableId={column.id} isDropDisabled={column.id === ANGEBOT_BOARD_COLUMN_ID}>
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      flex: 1,
                      bgcolor: snapshot.isDraggingOver
                        ? `${accent}08`
                        : bgColumn,
                      borderRadius: 2,
                      p: 1,
                      border: `1px dashed ${
                        snapshot.isDraggingOver ? accent : borderColor
                      }`,
                      transition: 'all 0.2s ease',
                      minHeight: 100,
                      overflowY: 'auto',
                      maxHeight: 'calc(100vh - 280px)',
                      '&::-webkit-scrollbar': { width: 6 },
                      '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                      '&::-webkit-scrollbar-thumb': {
                        bgcolor: borderColor,
                        borderRadius: 3,
                      },
                    }}
                  >
                    {column.items.map((projekt, index) => {
                      const typStyle = typChipSx(projekt.projekt_typ, isDark);
                      const isAngebot = projekt.source === 'angebot';
                      return (
                      <Draggable
                        key={boardRowKey(projekt)}
                        draggableId={boardRowKey(projekt)}
                        index={index}
                        isDragDisabled={isAngebot}
                      >
                        {(provided, snapshot) => (
                          <Paper
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...(!isAngebot ? provided.dragHandleProps : {})}
                            {...navigableProps(
                              navigate,
                              isAngebot ? `/angebote/${projekt.id}` : `/projekte/${projekt.id}`,
                            )}
                            elevation={snapshot.isDragging ? 8 : 0}
                            sx={{
                              p: 2,
                              mb: 1,
                              borderRadius: 2,
                              bgcolor: snapshot.isDragging
                                ? bgElevated
                                : bgCard,
                              border: `1px solid ${
                                snapshot.isDragging ? accent : borderColor
                              }`,
                              cursor: 'pointer',
                              transition: 'border-color 0.2s, box-shadow 0.2s',
                              transform: snapshot.isDragging
                                ? 'rotate(2deg)'
                                : 'none',
                              '&:hover': {
                                borderColor: `${accent}60`,
                                boxShadow: `0 0 0 1px ${accent}20`,
                              },
                            }}
                          >
                            {/* Project Name + Type */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.875rem',
                                    color: textPrimary,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {projekt.name}
                                </Typography>
                                {isAngebot && projekt.angebotNummer && (
                                  <Typography sx={{ fontSize: '0.7rem', color: textSecondary }}>
                                    {projekt.angebotNummer}
                                    {projekt.angebotPipelineStatus
                                      ? ` · ${angebotPipelineLabel(projekt.angebotPipelineStatus)}`
                                      : ''}
                                  </Typography>
                                )}
                              </Box>
                              <Chip
                                icon={typStyle.icon}
                                label={typStyle.label}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  flexShrink: 0,
                                  bgcolor: typStyle.bgcolor,
                                  color: typStyle.color,
                                  border: `1px solid ${typStyle.border}`,
                                  '& .MuiChip-icon': {
                                    color: 'inherit',
                                  },
                                }}
                              />
                            </Box>

                            {/* Kunde */}
                            {projekt.kunde && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  mb: 0.5,
                                }}
                              >
                                <BusinessIcon
                                  sx={{
                                    fontSize: 14,
                                    color: textSecondary,
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: '0.75rem',
                                    color: textSecondary,
                                  }}
                                >
                                  {projekt.kunde.name_des_unternehmens}
                                </Typography>
                              </Box>
                            )}

                            {/* Projektleiter */}
                            {projekt.projektleiter && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  mb: 1,
                                }}
                              >
                                <PersonIcon
                                  sx={{
                                    fontSize: 14,
                                    color: textSecondary,
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: '0.75rem',
                                    color: textSecondary,
                                  }}
                                >
                                  {projekt.projektleiter.vorname}{' '}
                                  {projekt.projektleiter.nachname}
                                </Typography>
                              </Box>
                            )}

                            {/* Bottom row: Personentage / Festpreis */}
                            {isAngebot && isFestpreisProjektTyp(projekt.projekt_typ) && projekt.nettobetrag != null ? (
                              <Typography
                                sx={{
                                  fontSize: '0.7rem',
                                  color: textSecondary,
                                  opacity: 0.7,
                                }}
                              >
                                {Number(projekt.nettobetrag).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                              </Typography>
                            ) : projekt.personentage != null && projekt.personentage > 0 ? (
                              <Typography
                                sx={{
                                  fontSize: '0.7rem',
                                  color: textSecondary,
                                  opacity: 0.7,
                                }}
                              >
                                {projekt.personentage.toLocaleString('de-DE', { maximumFractionDigits: 2 })} PT
                                {(projekt.personentage_onsite != null || projekt.personentage_remote != null) && (
                                  <> ({[
                                    projekt.personentage_onsite != null
                                      ? `${projekt.personentage_onsite.toLocaleString('de-DE', { maximumFractionDigits: 2 })} OS`
                                      : null,
                                    projekt.personentage_remote != null
                                      ? `${projekt.personentage_remote.toLocaleString('de-DE', { maximumFractionDigits: 2 })} R`
                                      : null,
                                  ].filter(Boolean).join(' / ')})</>
                                )}
                              </Typography>
                            ) : null}
                          </Paper>
                        )}
                      </Draggable>
                    );
                    })}
                    {provided.placeholder}

                    {column.items.length === 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: 80,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            color: textSecondary,
                            fontStyle: 'italic',
                          }}
                        >
                          {column.id === ANGEBOT_BOARD_COLUMN_ID ? 'Keine Angebote' : 'Keine Projekte'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Droppable>
            </Box>
          ))}
        </Box>
      </DragDropContext>
    </Box>
  );
};

export default ProjectKanbanView;
